import { loadingAsyncState } from 'widgets-for-react'
import {
  getTranslation,
  TranslationApiData,
  translationValidator,
} from '../../../utils/translation'
import { capitalize } from '../../../utils/utils'
import { canSafelyCast, TypeCastingValidator } from '../../../utils/safe-type-casting'
import { SeasonalCalendarData } from './seasonalCalendarState'

async function getSeasonalCalendarData(): Promise<any> {
  const res = await fetch('/api/v1/availability')
  if (!res.ok) throw Error(res.statusText)
  return await res.json()
}

type SeasonalCalendarApiData = {
  mainCategoryTitle: string
  mainCategoryTranslation: TranslationApiData
  subCategories: {
    subCategoryTranslation: TranslationApiData
    entries: {
      Characteristic: TranslationApiData
      Ingredient: TranslationApiData
      MonthlyAvailabilities: number[]
      Variant: TranslationApiData
    }[]
  }[]
}[]

const seasonalCalendarDataValidator: TypeCastingValidator<SeasonalCalendarApiData> =
  {
    kind: 'array',
    elementValidator: {
      kind: 'object',
      fieldsValidator: {
        mainCategoryTitle: { kind: 'string' },
        mainCategoryTranslation: translationValidator,
        subCategories: {
          kind: 'array',
          elementValidator: {
            kind: 'object',
            fieldsValidator: {
              subCategoryTranslation: translationValidator,
              entries: {
                kind: 'array',
                elementValidator: {
                  kind: 'object',
                  fieldsValidator: {
                    Characteristic: translationValidator,
                    Ingredient: translationValidator,
                    MonthlyAvailabilities: {
                      kind: 'array',
                      elementValidator: { kind: 'number' },
                    },
                    Variant: translationValidator,
                  },
                },
              },
            },
          },
        },
      },
    },
  }

export const loadSeasonalCalendarData = loadingAsyncState(() =>
  getSeasonalCalendarData().then((raw) => {
    const maybeSeasonalCalendarData = canSafelyCast<SeasonalCalendarApiData>(
      seasonalCalendarDataValidator,
      raw
    )
    return maybeSeasonalCalendarData.kind == 'r'
      ? {
          kind: 'result',
          value: translateSeasonalCalendarData(maybeSeasonalCalendarData.v)
            .map((mainCat) => ({
              ...mainCat,
              subcategories: mainCat.subcategories
                .map((subCat) => ({
                  ...subCat,
                  entries: subCat.entries.sort((fst, snd) =>
                    fst.ingredient.localeCompare(snd.ingredient)
                  ),
                }))
                .sort((fst, snd) =>
                  fst.subcategoryName.localeCompare(snd.subcategoryName)
                ),
            }))
            .sort((fst, snd) =>
              fst.mainCategoryName.localeCompare(snd.mainCategoryName)
            ),

          status: 200,
        }
      : {
          kind: 'failed',
          status: 404,
        }
  })
)

const translateSeasonalCalendarData = (
  data: SeasonalCalendarApiData
): SeasonalCalendarData =>
  data
    .map((mainCat) => ({
      mainCategoryTitle: mainCat.mainCategoryTitle,
      mainCategoryName: capitalize(
        getTranslation(mainCat.mainCategoryTranslation)
      ),
      subcategories: mainCat.subCategories.map((subCat) => ({
        subcategoryName: capitalize(
          getTranslation(subCat.subCategoryTranslation)
        ),
        entries: subCat.entries
          //when the entrie has a characteristic or a variant, but no translation of that in the current language, the entrie should not show
          .map((entry) => {
            const characteristicTranslation = getTranslation(
              entry.Characteristic
            )
            const variantTranslation = getTranslation(entry.Variant)
            return entry.Characteristic.length <= 0 ==
              (characteristicTranslation == '') &&
              entry.Variant.length <= 0 == (variantTranslation == '')
              ? [
                  {
                    characteristic: characteristicTranslation,
                    ingredient: capitalize(getTranslation(entry.Ingredient)),
                    variant: variantTranslation,
                    MonthlyAvailabilities: entry.MonthlyAvailabilities,
                  },
                ]
              : []
          })
          .reduce((prev, current) => prev.concat(current), []),
      })),
    }))
    //only show the (main and sub) categories and ingredients that have a translation in the current language
    .filter((maincat) => maincat.mainCategoryName)
    .map((maincat) => ({
      ...maincat,
      subcategories: maincat.subcategories
        .filter((subcat) => subcat.subcategoryName)
        .map((subcat) => ({
          ...subcat,
          entries: subcat.entries.filter((entry) => entry.ingredient),
        })),
    }))
